import React from 'react';
import { PlaybookAdherence } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';
import { ProgressBar, scoreTone } from '../../ui/ProgressBar';

interface QualifyingQuestionsSummaryCardProps {
  playbook?: PlaybookAdherence;
  discoveryPhaseTimestamp?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const QualifyingQuestionsSummaryCard: React.FC<QualifyingQuestionsSummaryCardProps> = ({
  playbook,
  discoveryPhaseTimestamp,
  onViewDetails,
  onViewInTimeline,
}) => {
  const hasGeneric = !!playbook?.question_generic;
  const hasPlaybook = playbook?.was_used && playbook?.question_coverage;
  const coveragePct = playbook?.question_coverage?.coverage_pct;
  const genericScore = playbook?.question_generic?.score;

  const displayScore = hasPlaybook && coveragePct != null ? coveragePct : genericScore;

  const badgeColor =
    displayScore == null
      ? 'bg-surface-subtle text-ink-muted border border-hairline'
      : displayScore >= 70
      ? 'bg-success-tint text-success border border-success'
      : displayScore >= 40
      ? 'bg-warning-tint text-warning border border-warning'
      : 'bg-error-tint text-error border border-error';

  const statusBadge = displayScore != null ? (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>{displayScore}%</span>
  ) : undefined;

  const coachingText = playbook?.question_generic?.coaching;

  const missingMsg =
    !playbook
      ? 'Qualifying questions analysis requires re-analysis of this session.'
      : !hasGeneric
      ? 'Re-analyze this session to see generic qualifying questions analysis.'
      : undefined;

  const resultContent = (
    <div className="space-y-3">
      {hasPlaybook && playbook.question_coverage && (
        <div className="bg-info-tint border border-info rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-info uppercase tracking-wider">Playbook Coverage</p>
            <span className="text-xs font-bold text-ink">
              {playbook.question_coverage.total_asked}/{playbook.question_coverage.total_available} questions
            </span>
          </div>
          <ProgressBar value={playbook.question_coverage.coverage_pct} tone={scoreTone(playbook.question_coverage.coverage_pct)} />
          {playbook.question_coverage.question_quality_summary && (
            <p className="text-xs text-ink mt-2">{playbook.question_coverage.question_quality_summary}</p>
          )}
        </div>
      )}
      {playbook?.question_generic && (
        <div className="bg-surface-subtle border border-hairline rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-wider">Generic Analysis</p>
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{genericScore}%</span>
          </div>
          <p className="text-xs text-ink-secondary">{playbook.question_generic.summary}</p>
          {(playbook.question_generic.what_worked || playbook.question_generic.what_missed) && (
            <div className="mt-2 space-y-1 pt-2 border-t border-hairline">
              {playbook.question_generic.what_worked && (
                <p className="text-[11px] text-success">
                  <span className="font-semibold">Worked: </span>{playbook.question_generic.what_worked}
                </p>
              )}
              {playbook.question_generic.what_missed && (
                <p className="text-[11px] text-error">
                  <span className="font-semibold">Missed: </span>{playbook.question_generic.what_missed}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Qualifying Questions"
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingText ? [coachingText] : undefined}
      sectionKey="qualifying"
      onViewDetails={onViewDetails}
      onViewInTimeline={discoveryPhaseTimestamp ? onViewInTimeline : undefined}
      missingDataMessage={missingMsg}
    />
  );
};

export default QualifyingQuestionsSummaryCard;
